import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getMyTelegram,
  createMyTelegramLink,
  updateMyTelegram,
  unlinkMyTelegram,
  sendMyTestDigest,
} from '../../services/api';

const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 6:00 … 22:00

// Сколько ждать, пока человек нажмёт «Старт» в боте, прежде чем перестать
// опрашивать статус. Код привязки живёт 30 минут, но держать опрос так долго
// незачем — есть кнопка «Проверить подключение».
const POLL_MS = 3000;
const POLL_LIMIT_MS = 3 * 60 * 1000;

// Что именно будет приходить — зависит от роли: выручка видна не всем,
// а напоминания о сменах есть только у тех, кто заведён в «Сотрудниках».
const describe = (state) => {
  if (!state) return 'Сообщения от бота вашего заведения.';
  const parts = [];
  if (state.can_digest) {
    parts.push('каждое утро — сводка: выручка за вчера против прогноза и прошлой недели, темп месяца к цели');
  }
  if (state.can_digest || state.can_kitchen) parts.push('днём — короткие оповещения о важном');
  if (state.has_employee) parts.push('вечером — напоминание о завтрашней смене');
  if (!parts.length) return 'Для вашей роли пока нечего присылать в Telegram.';
  const text = parts.join('; ');
  return text.charAt(0).toUpperCase() + text.slice(1) + '.';
};

const alertsLabel = (state) => {
  const items = [];
  if (state.can_digest) items.push('выручка дня отстаёт от прогноза больше чем на 20%');
  if (state.can_kitchen) items.push('позиция встала в стоп-лист');
  return `Оповещения: ${items.join(', ')}`;
};

const TelegramDigestCard = () => {
  const [state, setState] = useState(null); // ответ /api/me/telegram
  const [hidden, setHidden] = useState(false); // 403 — например, режим просмотра суперадмином
  const [linkUrl, setLinkUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await getMyTelegram();
      setState(res);
      return res;
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
      else setError('Не удалось загрузить настройки Telegram');
      return null;
    }
  }, []);

  useEffect(() => {
    load();
    return stopPolling;
  }, [load]);

  const startLink = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const { url } = await createMyTelegramLink();
      setLinkUrl(url);
      window.open(url, '_blank', 'noopener');
      const startedAt = Date.now();
      stopPolling();
      pollRef.current = setInterval(async () => {
        const res = await load();
        if (res?.linked || Date.now() - startedAt > POLL_LIMIT_MS) {
          stopPolling();
          if (res?.linked) setLinkUrl('');
        }
      }, POLL_MS);
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось получить ссылку');
    } finally {
      setBusy(false);
    }
  };

  const save = async (patch) => {
    setError('');
    setNotice('');
    setState(prev => ({ ...prev, ...patch }));
    try {
      setState(await updateMyTelegram(patch));
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось сохранить');
      load();
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await sendMyTestDigest();
      setNotice('Сводка отправлена — проверьте Telegram');
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось отправить сводку');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Отвязать Telegram? Сводки и оповещения перестанут приходить.')) return;
    setBusy(true);
    try {
      await unlinkMyTelegram();
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (hidden) return null;

  return (
    <section className="ui-card">
      <header className="ui-card-head">
        <h2>Telegram</h2>
        <p>{describe(state)}</p>
      </header>

      <div className="tgd-body">
        {!state && !error && <p className="ui-dim">Проверяем…</p>}

        {state && !state.bot_connected && (
          <p className="ui-dim">
            Telegram-бот организации ещё не подключён. Его подключает администратор
            в разделе «Пользователи» → «Интеграции».
          </p>
        )}

        {state && state.bot_connected && !state.linked && (
          <>
            <button type="button" className="ui-btn-primary tgd-connect" onClick={startLink} disabled={busy}>
              {busy ? 'Готовим ссылку…' : `Подключить @${state.bot_username}`}
            </button>
            {linkUrl && (
              <div className="tgd-waiting">
                <p>
                  В Telegram откроется бот — нажмите «Старт». Если окно не открылось,{' '}
                  <a href={linkUrl} target="_blank" rel="noopener noreferrer">перейдите по ссылке</a>.
                  Ссылка действует 30 минут.
                </p>
                <button type="button" className="ui-btn-ghost" onClick={load}>Проверить подключение</button>
              </div>
            )}
          </>
        )}

        {state && state.linked && (
          <>
            <div className="tgd-linked">
              <span className="ui-badge role-default">
                Подключено{state.username ? ` · @${state.username}` : ''}
              </span>
              <button type="button" className="ui-btn-ghost" onClick={unlink} disabled={busy}>Отвязать</button>
            </div>

            {state.can_digest && (
            <label className="tgd-row">
              <input
                type="checkbox"
                checked={state.digest_enabled}
                onChange={e => save({ digest_enabled: e.target.checked })}
              />
              <span>Утренняя сводка в</span>
              <select
                className="tgd-hour"
                value={state.digest_hour}
                disabled={!state.digest_enabled}
                onChange={e => save({ digest_hour: Number(e.target.value) })}
                aria-label="Час отправки сводки"
              >
                {HOURS.map(h => <option key={h} value={h}>{`${h}:00`}</option>)}
              </select>
            </label>
            )}

            {(state.can_digest || state.can_kitchen) && (
              <label className="tgd-row">
                <input
                  type="checkbox"
                  checked={state.alerts_enabled}
                  onChange={e => save({ alerts_enabled: e.target.checked })}
                />
                <span>{alertsLabel(state)}</span>
              </label>
            )}

            {state.has_employee && (
              <label className="tgd-row">
                <input
                  type="checkbox"
                  checked={state.shift_reminders}
                  onChange={e => save({ shift_reminders: e.target.checked })}
                />
                <span>Напоминать о завтрашней смене — вечером, в 18:00</span>
              </label>
            )}

            {state.can_digest && (
              <div>
                <button type="button" className="ui-btn-ghost" onClick={sendTest} disabled={busy}>
                  {busy ? 'Отправляем…' : 'Прислать сводку сейчас'}
                </button>
              </div>
            )}
          </>
        )}

        {notice && <p className="ui-inline-success tgd-msg">{notice}</p>}
        {error && <p className="ui-hint-error">{error}</p>}
      </div>
    </section>
  );
};

export default TelegramDigestCard;
